/**
 * Board desync diagnostics module.
 *
 * Active when VITE_GAME_DIAGNOSTICS=true is set at build time, or when
 * running in Vite dev mode. Production builds with neither condition have
 * zero runtime overhead — Vite eliminates all GAME_DIAGNOSTICS-gated
 * blocks at build time via dead-code elimination.
 *
 * Console API (staging):
 *   window.__DOMINO_DIAGNOSTICS__.summary()
 *   window.__DOMINO_DIAGNOSTICS__.dumpBoard()
 *   window.__DOMINO_DIAGNOSTICS__.dumpTransactions()
 *   window.__DOMINO_DIAGNOSTICS__.dumpMisplacements()
 *   window.__DOMINO_DIAGNOSTICS__.dumpPerformance()
 *   window.__DOMINO_DIAGNOSTICS__.clear()
 */

import { getBoardUnitSize } from '../utils/coordinates';
import { readGameExitHistory } from './exitBreadcrumbs';

// Diagnostic policy — repository-controlled, no environment variables:
//  - Local development (any non-production build): full diagnostics.
//  - staging.thedominogame.com: full diagnostics, permanently — staging is
//    the permanent testing environment for all future changes.
//  - Any other production hostname: the detailed diagnostic interface is
//    disabled. Production keeps only the passive, bounded exit breadcrumbs
//    (debug/exitBreadcrumbs.ts, deliberately ungated) plus essential
//    warnings/errors.
export const GAME_DIAGNOSTICS: boolean =
  !import.meta.env.PROD ||
  (typeof window !== 'undefined' && window.location.hostname === 'staging.thedominogame.com');

// ─── Cap constants ────────────────────────────────────────────
const MAX_TXN       = 200;
const MAX_TILES     = 500;
const MAX_DETECTS   = 200;
const MAX_ACTIONS   = 100;
const MAX_FRAMES    = 300;   // ~5 s at 60 fps

// ─── Types ────────────────────────────────────────────────────
export type TransactionSource =
  | 'hydration-rest'
  | 'ws-initial-state'
  | 'ws-game-update'
  | 'ws-game-finished'
  | 'reconnect-rest'
  | 'resize'
  | 'round-transition';

export interface BoardTransform {
  x: number; y: number; scaleX: number; scaleY: number;
}

export interface TxRecord {
  txnId:                  number;
  source:                 TransactionSource;
  gameId:                 number;
  gameNumber?:            number;
  currentTurn:            number;
  tileCount:              number;
  boardUnit:              number;
  prevBoardUnit?:         number;
  receivedTs:             number;
  applyStartTs:           number;
  renderBoardStartTs?:    number;
  renderBoardEndTs?:      number;
  autoFitStartTs?:        number;
  autoFitEndTs?:          number;
  boardBefore?:           BoardTransform;
  boardAfter?:            BoardTransform;
  newTileIds:             number[];
  hadActiveTravelTween:   boolean;
  resizeOccurredDuringTx: boolean;
  /** WS revision counter before subscribe() was called (the authoritative ordering baseline). */
  wsRevBeforeSubscription?:    number;
  /** WS revision counter immediately after subscribe() returned (to detect WS-during-subscribe). */
  wsRevAfterSubscription?:     number;
  /** WS revision counter at the moment the REST response was received. */
  wsRevAtResponse?:            number;
  /** True when wsRevAfterSubscription > wsRevBeforeSubscription — WS state was applied during subscription setup. */
  wsAppliedDuringSubscription?: boolean;
  /** True when wsRevAtResponse > wsRevAfterSubscription — WS state was applied while REST was in flight. */
  wsAppliedDuringRestAwait?:   boolean;
  /** True when wsRevAtResponse > wsRevBeforeSubscription — WS applied at any point; this is the ordering decision basis. */
  wsAppliedAny?:               boolean;
  /** game_number from the WS currentGameState at the moment of the REST ordering decision. */
  wsStateGameNumber?:          number;
  /** True when this REST public board was discarded because WS applied newer state during the REST await. */
  restDiscarded?:          boolean;
  /** Human-readable reason the REST board was discarded. */
  rejectionReason?:        string;
  // ── Resize-specific fields (only populated when source === 'resize') ──────
  /** Viewport width at the time of the resize. */
  resizeViewportW?:        number;
  /** Viewport height at the time of the resize. */
  resizeViewportH?:        number;
  /** Board container X origin after setPosition(). */
  resizeBoardX?:           number;
  /** Board container Y origin after setPosition(). */
  resizeBoardY?:           number;
  /** Number of existing tile sprites reprojected from canonical state. */
  resizeReprojected?:      number;
  /** True when an active travel tween was snapped (stopped) to handle the resize. */
  resizeTravelSnapped?:    boolean;
  /** Tile ID that was snapped, if any. */
  resizeSnappedTileId?:    number;
  /** Number of tiles failing the post-reproject invariant check (expected 0). */
  resizeInvariantViolations?: number;
}

export interface TilePlacementRecord {
  txnId:               number;
  tileId:              number;
  valueA:              number;
  valueB:              number;
  engineX:             number;
  engineY:             number;
  expectedLocalX:      number;
  expectedLocalY:      number;
  actualLocalX:        number;
  actualLocalY:        number;
  boardXAtCreation:    number;
  boardYAtCreation:    number;
  boardScaleAtCreation: number;
  boardUnitAtCreation: number;
  ghostDestX?:         number;
  ghostDestY?:         number;
  travelStartTs?:      number;
  travelEndTs?:        number;
  finalWorldX?:        number;
  finalWorldY?:        number;
  destDeltaX?:         number;
  destDeltaY?:         number;
  angle:               number;
  scaleX:              number;
  scaleY:              number;
  alpha:               number;
  depth:               number;
  activeTweensAtSettle?: number;
  misplacement:        boolean;
}

export type DetectionType =
  | 'REST_OLDER_THAN_WS'
  | 'REST_PUBLIC_STATE_DISCARDED_AFTER_WS'
  | 'TILE_COUNT_DECREASED'
  | 'DUPLICATE_TILE_ID'
  | 'BOARD_MOVED_DURING_TRAVEL'
  | 'BOARD_SCALE_CHANGED_DURING_TRAVEL'
  | 'RESIZE_DURING_TRAVEL'
  | 'MISPLACEMENT_DETECTED'
  | 'STALE_UNIT_SIZE_AFTER_RESIZE'
  | 'STALE_TWEEN_AFTER_NEWER_TXN'
  | 'CONCURRENT_RENDER_TRANSACTIONS'
  | 'RESIZE_INVARIANT_VIOLATION'
  | 'NO_TRAVEL_SOURCE';

export interface DetectionRecord {
  ts:     number;
  type:   DetectionType;
  detail: Record<string, unknown>;
}

export interface ActionTimingRecord {
  tileId?:                  number;
  pointerDownTs?:           number;
  liftStartTs?:             number;
  releaseTs?:               number;
  requestSentTs?:           number;
  restResponseTs?:          number;
  wsUpdateTs?:              number;
  /** Timestamp at entry to applyGameState() — first JS work after WS dispatch. */
  applyGameStateStartedAt?: number;
  /** Timestamp immediately before board.renderBoard() is called. */
  boardRenderStartedAt?:    number;
  renderStartTs?:           number;
  travelStartTs?:           number;
  /** Timestamp immediately after scene.tweens.add() returns the travel tween. */
  travelTweenCreatedAt?:    number;
  /** Timestamp of the first onUpdate tick of the travel tween — first rendered movement frame. */
  travelFirstUpdateAt?:     number;
  travelEndTs?:             number;
  settleTs?:                number;
  /** How the player committed this move. */
  inputMethod?:             'click' | 'drag';
  /** True when this record was created from a WS update with no preceding pointer event. */
  isOpponentMove?:          boolean;
  /** Internal: generation token matching the tween that owns this record's travelFirstUpdateAt slot. */
  _tweenGen?:               number;
}

export interface FramePerfRecord {
  fps:   number;
  delta: number;
  ts:    number;
}

// ─── Internal state ───────────────────────────────────────────
let _txnCounter       = 0;
let _currentTxnId     = -1;
let _renderInProgress = false;

// One record per received authoritative state — accepted or rejected by the
// state-sequence gate. Bounded like every other buffer.
export interface StateAcceptanceRecord {
  ts:                  number;
  source:              string;   // 'rest' | 'initial_state' | 'game_update' | 'game_finished' | 'reconnect' | 'visibility_resume'
  gameId:              number;
  sessionId:           number | null;
  stateSeq:            number | null;
  lastAppliedBefore:   number;
  accepted:            boolean;
  reason:              string;
  turn:                number | undefined;
  tileCount:           number;
  sceneInstanceId:     number;
  subscriptionId:      string;
  visibility:          string;
  wsConnected:         boolean;
  firstRender:         boolean;
  animationsSuppressed: boolean;
}
const MAX_STATE_ACCEPT = 200;
const _stateAcceptLog: StateAcceptanceRecord[] = [];

const _txnLog:        TxRecord[]            = [];
const _tilePlacements: TilePlacementRecord[] = [];
const _detections:    DetectionRecord[]      = [];
const _framePerf:     FramePerfRecord[]      = [];
const _actionLog:     ActionTimingRecord[]   = [];

// tileId → { engineX, engineY } — registered when a tile is first placed
const _tileEngineCoords = new Map<number, { engineX: number; engineY: number }>();

// tileId → travel info while ghost is in flight
const _activeTravels = new Map<number, {
  txnId:           number;
  destX:           number;
  destY:           number;
  boardAtCreation: BoardTransform;
}>();

let _activeAction: ActionTimingRecord | null = null;
let _lastWsTileCount  = -1;
// Monotonically increasing counter. Incremented each time a travel tween is created
// for _activeAction. The tween's onUpdate closure captures its generation and only
// stamps travelFirstUpdateAt if the generation still matches the current _activeAction,
// preventing a stale tween from corrupting a newer move's record.
let _tweenGenCounter = 0;

// Lightweight interface refs — typed structurally to avoid importing Phaser
interface BoardRef {
  x: number; y: number; scaleX: number; scaleY: number;
  getTileSprites(): ReadonlyMap<number, {
    x: number; y: number; alpha: number; depth: number; visible: boolean;
    orientation: string;
    scene?: { tweens?: { getTweensOf?(t: unknown): unknown[] } };
  }>;
}
interface SceneRef {
  children?: { list?: unknown[] };
  cameras?: { main?: { zoom?: number } };
  scale?: { width?: number; height?: number };
}
interface GameRef {
  loop?: { actualFps?: number; delta?: number };
}

let _boardRef: BoardRef | null = null;
let _sceneRef: SceneRef | null = null;
let _gameRef:  GameRef  | null = null;

// rAF frame-perf loop
let _rafHandle: number | null = null;
let _lastRafTs = 0;

// ─── Helpers ──────────────────────────────────────────────────
function _push<T>(arr: T[], item: T, max: number): void {
  arr.push(item);
  if (arr.length > max) arr.shift();
}

function _findTxn(txnId: number): TxRecord | undefined {
  for (let i = _txnLog.length - 1; i >= 0; i--) {
    if (_txnLog[i].txnId === txnId) return _txnLog[i];
  }
  return undefined;
}

function _detect(type: DetectionType, detail: Record<string, unknown>): void {
  _push(_detections, { ts: performance.now(), type, detail }, MAX_DETECTS);
  console.warn(`[DominoDiagnostics] ${type}`, detail);
}

// ─── Frame perf rAF loop ──────────────────────────────────────
function _tickFramePerf(ts: number): void {
  if (_lastRafTs > 0) {
    const delta = ts - _lastRafTs;
    _push(_framePerf, { fps: 1000 / delta, delta, ts }, MAX_FRAMES);
  }
  _lastRafTs = ts;
  _rafHandle = requestAnimationFrame(_tickFramePerf);
}

function _ensureRaf(): void {
  if (_rafHandle === null) _rafHandle = requestAnimationFrame(_tickFramePerf);
}

function _stopRaf(): void {
  if (_rafHandle !== null) {
    cancelAnimationFrame(_rafHandle);
    _rafHandle = null;
    _lastRafTs = 0;
  }
}

function _computeFrameStats() {
  if (_framePerf.length === 0) return null;
  const fpsSorted = _framePerf.map(f => f.fps).sort((a, b) => a - b);
  const avg     = fpsSorted.reduce((s, v) => s + v, 0) / fpsSorted.length;
  const p5      = fpsSorted[Math.floor(fpsSorted.length * 0.05)] ?? fpsSorted[0];
  const deltas  = _framePerf.map(f => f.delta);
  const maxDelta  = Math.max(...deltas);
  const medDelta  = [...deltas].sort((a, b) => a - b)[Math.floor(deltas.length / 2)];
  const spikes25  = _framePerf.filter(f => f.delta > 25).length;
  const spikes50  = _framePerf.filter(f => f.delta > 50).length;
  const spikes100 = _framePerf.filter(f => f.delta > 100).length;
  return { avg, p5, maxDelta, medDelta, spikes25, spikes50, spikes100, sampleCount: _framePerf.length };
}

// ─── Public API object ────────────────────────────────────────
export const diag = {

  // ── Scene lifecycle ────────────────────────────────────────

  attach(scene: unknown, board: unknown, game: unknown): void {
    _sceneRef = scene as SceneRef;
    _boardRef = board as BoardRef;
    _gameRef  = game  as GameRef;
    _ensureRaf();
  },

  detach(): void {
    _sceneRef = null;
    _boardRef = null;
    _stopRaf();
  },

  // ── Transaction lifecycle ──────────────────────────────────

  beginTransaction(
    source:                    TransactionSource,
    gameId:                    number,
    gameNumber:                number | undefined,
    currentTurn:               number,
    tileCount:                 number,
    boardUnit:                 number,
    receivedTs:                number,
    prevBoardUnit?:            number,
    wsRevBeforeSubscription?:  number,
    wsRevAfterSubscription?:   number,
    wsRevAtResponse?:          number,
  ): number {
    const txnId = ++_txnCounter;
    _currentTxnId = txnId;

    if (_renderInProgress) {
      _detect('CONCURRENT_RENDER_TRANSACTIONS', {
        newTxnId: txnId, source,
        description: 'beginTransaction called while a prior transaction is still open',
      });
    }
    _renderInProgress = true;

    const wsAppliedDuringSubscription =
      wsRevBeforeSubscription !== undefined && wsRevAfterSubscription !== undefined
        ? wsRevAfterSubscription > wsRevBeforeSubscription
        : undefined;

    const wsAppliedDuringRestAwait =
      wsRevAfterSubscription !== undefined && wsRevAtResponse !== undefined
        ? wsRevAtResponse > wsRevAfterSubscription
        : (wsRevBeforeSubscription !== undefined && wsRevAtResponse !== undefined
            ? wsRevAtResponse > wsRevBeforeSubscription
            : undefined);

    const wsAppliedAny =
      wsRevBeforeSubscription !== undefined && wsRevAtResponse !== undefined
        ? wsRevAtResponse > wsRevBeforeSubscription
        : undefined;

    _push(_txnLog, {
      txnId, source, gameId, gameNumber, currentTurn, tileCount, boardUnit,
      prevBoardUnit,
      wsRevBeforeSubscription, wsRevAfterSubscription, wsRevAtResponse,
      wsAppliedDuringSubscription, wsAppliedDuringRestAwait, wsAppliedAny,
      receivedTs, applyStartTs: performance.now(),
      newTileIds: [], hadActiveTravelTween: _activeTravels.size > 0,
      resizeOccurredDuringTx: false,
    }, MAX_TXN);

    return txnId;
  },

  endTransaction(txnId: number): void {
    if (_currentTxnId === txnId) _renderInProgress = false;
  },

  setRenderBoardStart(txnId: number): void {
    const r = _findTxn(txnId);
    if (r) r.renderBoardStartTs = performance.now();
  },

  setRenderBoardEnd(txnId: number): void {
    const r = _findTxn(txnId);
    if (r) r.renderBoardEndTs = performance.now();
  },

  setAutoFitTimes(
    txnId:  number,
    startTs: number,
    endTs:   number,
    before:  BoardTransform,
    after:   BoardTransform,
  ): void {
    const r = _findTxn(txnId);
    if (r) {
      r.autoFitStartTs = startTs;
      r.autoFitEndTs   = endTs;
      r.boardBefore    = before;
      r.boardAfter     = after;
    }

    const boardMoved   = Math.abs(after.x - before.x) > 0.5 || Math.abs(after.y - before.y) > 0.5;
    const scaleChanged = Math.abs(after.scaleX - before.scaleX) > 0.001;

    if (_activeTravels.size > 0) {
      const activeIds = [..._activeTravels.keys()];
      if (boardMoved) {
        _detect('BOARD_MOVED_DURING_TRAVEL', {
          txnId, activeIds,
          deltaX: after.x - before.x, deltaY: after.y - before.y,
          description: 'autoFitToPlayArea shifted the board while ghost tweens are in flight — ghost destination now stale',
        });
        if (r) r.hadActiveTravelTween = true;
      }
      if (scaleChanged) {
        _detect('BOARD_SCALE_CHANGED_DURING_TRAVEL', {
          txnId, activeIds,
          scaleBefore: before.scaleX, scaleAfter: after.scaleX,
          description: 'autoFitToPlayArea rescaled the board while ghost tweens are in flight',
        });
        if (r) r.hadActiveTravelTween = true;
      }
    }
  },

  flagResizeDuringTransaction(txnId: number): void {
    const r = _findTxn(txnId);
    if (r) r.resizeOccurredDuringTx = true;
    if (_activeTravels.size > 0) {
      _detect('RESIZE_DURING_TRAVEL', {
        txnId, activeIds: [..._activeTravels.keys()],
        description: 'handleResize fired while ghost travel tweens are in flight',
      });
    }
  },

  /** Mark a hydration-rest or reconnect-rest transaction as discarded because WS applied state during the REST await. */
  markRestDiscarded(
    txnId:                    number,
    reason:                   string,
    wsRevBeforeSubscription:  number,
    wsRevAtResponse:          number,
    restTileCount:            number,
    wsTileCount:              number,
    restGameNumber?:          number,
    wsStateGameNumber?:       number,
  ): void {
    const r = _findTxn(txnId);
    if (r) {
      r.restDiscarded     = true;
      r.rejectionReason   = reason;
      r.wsStateGameNumber = wsStateGameNumber;
    }
    _detect('REST_PUBLIC_STATE_DISCARDED_AFTER_WS', {
      txnId, reason,
      wsRevBeforeSubscription, wsRevAtResponse,
      wsAppliedCount:    wsRevAtResponse - wsRevBeforeSubscription,
      restTileCount,     wsTileCount,
      restGameNumber:    restGameNumber    ?? '-',
      wsStateGameNumber: wsStateGameNumber ?? '-',
      description: 'REST public board discarded: WS state was applied after subscription baseline and before REST resolved',
    });
  },

  // ── Tile registration ──────────────────────────────────────

  registerTileEngineCoords(tileId: number, engineX: number, engineY: number): void {
    _tileEngineCoords.set(tileId, { engineX, engineY });
  },

  recordTilePlacement(p: TilePlacementRecord): void {
    const r = _findTxn(p.txnId);
    if (r && !r.newTileIds.includes(p.tileId)) r.newTileIds.push(p.tileId);

    const existing = _tilePlacements.findIndex(
      t => t.tileId === p.tileId && t.travelEndTs === undefined,
    );
    if (existing >= 0) {
      _detect('DUPLICATE_TILE_ID', {
        tileId: p.tileId, newTxnId: p.txnId,
        description: 'Tile placed while another in-flight record for the same id exists',
      });
    }
    _push(_tilePlacements, p, MAX_TILES);
  },

  // ── Travel tween lifecycle ─────────────────────────────────

  notifyTravelStart(
    txnId:           number,
    tileId:          number,
    destX:           number,
    destY:           number,
    boardAtCreation: BoardTransform,
  ): void {
    _activeTravels.set(tileId, { txnId, destX, destY, boardAtCreation });
    const rec = _findTilePlacement(tileId, txnId);
    if (rec) {
      rec.ghostDestX    = destX;
      rec.ghostDestY    = destY;
      rec.travelStartTs = performance.now();
    }
    // Also stamp the active action so travelStartTs is populated (was previously unset).
    if (_activeAction && !_activeAction.travelStartTs) {
      _activeAction.travelStartTs = performance.now();
    }
  },

  notifyTravelEnd(
    txnId:        number,
    tileId:       number,
    finalWorldX:  number,
    finalWorldY:  number,
    activeTweens: number,
  ): void {
    _activeTravels.delete(tileId);
    const rec = _findTilePlacement(tileId, txnId);
    if (!rec) {
      // Travel completed but no placement record found (txnId mismatch or record
      // evicted). Clear _activeAction so its stale timestamps cannot contaminate
      // the next move — leaving it alive is the root cause of negative timings.
      _activeAction = null;
      return;
    }

    rec.travelEndTs          = performance.now();
    rec.finalWorldX          = finalWorldX;
    rec.finalWorldY          = finalWorldY;
    rec.activeTweensAtSettle = activeTweens;

    if (rec.ghostDestX !== undefined && rec.ghostDestY !== undefined) {
      rec.destDeltaX = finalWorldX - rec.ghostDestX;
      rec.destDeltaY = finalWorldY - rec.ghostDestY;
      const mag = Math.sqrt(rec.destDeltaX ** 2 + rec.destDeltaY ** 2);
      if (mag > 1.0) {
        rec.misplacement = true;
        _detect('MISPLACEMENT_DETECTED', {
          tileId, txnId,
          ghostDest:   { x: rec.ghostDestX, y: rec.ghostDestY },
          finalWorld:  { x: finalWorldX, y: finalWorldY },
          delta:       { x: rec.destDeltaX, y: rec.destDeltaY, magnitude: mag },
          description: 'Ghost tween landed at a different world position than the real tile — visual desync',
        });
      }
    }

    if (txnId < _currentTxnId) {
      _detect('STALE_TWEEN_AFTER_NEWER_TXN', {
        travelTxnId: txnId, currentTxnId: _currentTxnId, tileId,
        description: 'Travel tween completed for an older transaction after a newer one was applied',
      });
    }

    // Forward settle time to active action if one is open
    if (_activeAction && !_activeAction.travelEndTs) {
      _activeAction.travelEndTs = rec.travelEndTs;
      _activeAction.settleTs    = rec.travelEndTs;
      // Validate intervals before logging — discard samples with physically
      // impossible negative timings, which indicate a stale _activeAction that
      // survived a missed early-return from a previous cycle.
      const iv = _computeSampleIntervals(_activeAction);
      const corrupt =
        (iv.ws_firstFrame   !== null && iv.ws_firstFrame   < 0) ||
        (iv.tween_firstFrame !== null && iv.tween_firstFrame < 0) ||
        (iv.req_ws           !== null && iv.req_ws           < 0);
      if (!corrupt) {
        _push(_actionLog, _activeAction, MAX_ACTIONS);
      } else {
        console.warn('[DominoDiagnostics] Discarding corrupt action sample (negative interval):', iv);
      }
      _activeAction = null;
    }
  },

  checkTileLocalPosition(tileId: number, actualLocalX: number, actualLocalY: number, newBoardUnit: number): void {
    const coords = _tileEngineCoords.get(tileId);
    if (!coords) return;
    const expX = coords.engineX * newBoardUnit;
    const expY = coords.engineY * newBoardUnit;
    const dx   = actualLocalX - expX;
    const dy   = actualLocalY - expY;
    if (Math.abs(dx) > 1.0 || Math.abs(dy) > 1.0) {
      _detect('STALE_UNIT_SIZE_AFTER_RESIZE', {
        tileId,
        expectedLocal: { x: expX, y: expY },
        actualLocal:   { x: actualLocalX, y: actualLocalY },
        delta:         { x: dx, y: dy },
        newBoardUnit,
        description: 'Tile local position does not match engineCoords × newBoardUnit — existing tile stale after resize',
      });
    }
  },

  // ── Resize reproject ──────────────────────────────────────

  /** Clears the snapped tileId from activeTravels to prevent false stale-tween detections. */
  notifyTravelSnapped(tileId: number): void {
    _activeTravels.delete(tileId);
    // tween.stop() does not call onComplete, so notifyTravelEnd will never fire.
    // Clear _activeAction here so its timestamps (including travelFirstUpdateAt)
    // cannot contaminate the next move's timing record.
    _activeAction = null;
  },

  /** Records resize-specific data into the current resize TxRecord. */
  recordResizeReproject(txnId: number, params: {
    viewportW:     number;
    viewportH:     number;
    boardX:        number;
    boardY:        number;
    reprojected:   number;
    travelSnapped: boolean;
    snappedTileId?: number;
  }): void {
    const tx = _txnLog.find(t => t.txnId === txnId);
    if (!tx) return;
    tx.resizeViewportW     = params.viewportW;
    tx.resizeViewportH     = params.viewportH;
    tx.resizeBoardX        = params.boardX;
    tx.resizeBoardY        = params.boardY;
    tx.resizeReprojected   = params.reprojected;
    tx.resizeTravelSnapped = params.travelSnapped;
    if (params.snappedTileId !== undefined) tx.resizeSnappedTileId = params.snappedTileId;
  },

  /**
   * Post-reproject invariant check: fires RESIZE_INVARIANT_VIOLATION for any tile
   * whose sprite position still deviates from engineCoord × unit after reprojection.
   * Expected to report 0 violations if reprojectPlacedTiles() ran correctly.
   */
  checkResizeInvariant(
    txnId: number,
    tiles: Array<{ id: number; position: { x: number; y: number } }>,
    sprites: ReadonlyMap<number, { x: number; y: number }>,
    unit: number,
  ): void {
    let violations = 0;
    for (const td of tiles) {
      const sp = sprites.get(td.id);
      if (!sp) continue;
      const expX = td.position.x * unit;
      const expY = td.position.y * unit;
      const dx   = sp.x - expX;
      const dy   = sp.y - expY;
      if (Math.abs(dx) > 1.0 || Math.abs(dy) > 1.0) {
        violations++;
        _detect('RESIZE_INVARIANT_VIOLATION', {
          tileId: td.id, expX, expY, actualX: sp.x, actualY: sp.y, dx, dy, unit, txnId,
        });
      }
    }
    const tx = _txnLog.find(t => t.txnId === txnId);
    if (tx) tx.resizeInvariantViolations = violations;
    if (violations > 0) {
      console.warn(`[DominoDiagnostics] RESIZE_INVARIANT_VIOLATION: ${violations} tile(s) still misaligned after reprojectPlacedTiles()`);
    }
  },

  // ── WS / REST ordering ────────────────────────────────────

  recordWsState(tileCount: number): void {
    _lastWsTileCount = Math.max(_lastWsTileCount, tileCount);
  },

  /**
   * A new board tile arrived with no travel source under the primary rule
   * (prevTurn was this player or undefined — batched/coalesced update or
   * turn-timer auto-play). A fallback source was applied; detail.reason says
   * which flavor. Without the fallback this tile would have popped in with
   * no travel animation.
   */
  recordNoTravelSource(detail: Record<string, unknown>): void {
    _detect('NO_TRAVEL_SOURCE', detail);
  },

  /** One entry per received authoritative state, with the gate verdict.
   *  Rejections are also warned so they are visible in live consoles. */
  recordStateAcceptance(rec: Omit<StateAcceptanceRecord, 'ts'>): void {
    _push(_stateAcceptLog, { ts: performance.now(), ...rec }, MAX_STATE_ACCEPT);
    if (!rec.accepted) {
      console.warn(
        `[DominoDiagnostics] STATE_REJECTED source=${rec.source} seq=${rec.stateSeq}` +
        ` lastApplied=${rec.lastAppliedBefore} reason=${rec.reason} game=${rec.gameId}`,
      );
    }
  },

  checkRestOrdering(restTileCount: number, renderedTileCount: number): void {
    if (restTileCount < renderedTileCount) {
      _detect('REST_OLDER_THAN_WS', {
        restTileCount, renderedTileCount, wsMaxTileCount: _lastWsTileCount,
        description: 'REST response has fewer board tiles than already rendered via WS — REST snapshot is older',
      });
    }
  },

  checkTileCountDecreased(prevCount: number, newCount: number, source: TransactionSource): void {
    if (newCount < prevCount && prevCount > 0) {
      _detect('TILE_COUNT_DECREASED', {
        prevCount, newCount, source,
        description: 'Board tile count decreased — tiles may be removed by a stale state update',
      });
    }
  },

  // ── Action timing ──────────────────────────────────────────

  recordPointerDown(tileId: number): void {
    _activeAction = { tileId, pointerDownTs: performance.now() };
  },

  recordLiftStart(): void {
    if (_activeAction) _activeAction.liftStartTs = performance.now();
  },

  recordRelease(): void {
    if (_activeAction) _activeAction.releaseTs = performance.now();
  },

  recordRequestSent(): void {
    if (_activeAction) _activeAction.requestSentTs = performance.now();
  },

  recordRestResponse(): void {
    if (_activeAction) _activeAction.restResponseTs = performance.now();
  },

  recordWsUpdate(): void {
    const ts = performance.now();
    if (_activeAction) {
      // If travelFirstUpdateAt or travelEndTs is already stamped, this _activeAction
      // belongs to a previous move whose travel tween has already started or finished.
      // The current WS message is for a NEW move — discard the stale record to prevent
      // its travelFirstUpdateAt from producing a negative ws_firstFrame interval.
      if (
        _activeAction.travelFirstUpdateAt !== undefined ||
        _activeAction.travelEndTs        !== undefined
      ) {
        _activeAction = null;
      } else {
        // Valid in-flight local-move record (pointer down → request sent → WS arriving).
        // Stamp wsUpdateTs and continue accumulating on the same record.
        _activeAction.wsUpdateTs = ts;
        return;
      }
    }
    // No active action (or just cleared a stale one) — this is an opponent move.
    _activeAction = { wsUpdateTs: ts, isOpponentMove: true };
  },

  recordRenderStart(): void {
    if (_activeAction) _activeAction.renderStartTs = performance.now();
  },

  recordTravelStartForAction(): void {
    if (_activeAction) _activeAction.travelStartTs = performance.now();
  },

  // ── Pre-travel latency instrumentation ────────────────────

  /** Called at the top of applyGameState(), before any work is done. */
  recordApplyGameStateStart(): void {
    if (_activeAction) _activeAction.applyGameStateStartedAt = performance.now();
  },

  /** Called immediately before board.renderBoard() in renderBoardFromLayout(). */
  recordBoardRenderStart(): void {
    if (_activeAction) _activeAction.boardRenderStartedAt = performance.now();
  },

  /** Called immediately after scene.tweens.add() returns the travel tween object.
   *  Returns a generation token that must be passed to recordTravelFirstUpdate()
   *  so only the correct tween can stamp travelFirstUpdateAt on the current record. */
  recordTravelTweenCreated(): number {
    _tweenGenCounter++;
    if (_activeAction) {
      _activeAction.travelTweenCreatedAt = performance.now();
      _activeAction._tweenGen = _tweenGenCounter;
    }
    return _tweenGenCounter;
  },

  /** Called from the tween's onUpdate callback on the first tick — the first rendered movement frame.
   *  gen: generation token returned by recordTravelTweenCreated(); only stamps if it matches
   *  the current _activeAction._tweenGen, preventing a stale tween from corrupting a newer record. */
  recordTravelFirstUpdate(gen: number): void {
    if (
      _activeAction &&
      _activeAction.travelFirstUpdateAt === undefined &&
      _activeAction._tweenGen === gen
    ) {
      _activeAction.travelFirstUpdateAt = performance.now();
    }
  },

  /** Called from confirmPlay() to record whether the commit was tap/click or drag-and-drop. */
  recordInputMethod(method: 'click' | 'drag'): void {
    if (_activeAction) _activeAction.inputMethod = method;
  },

  // ── Read-only accessors ────────────────────────────────────

  get currentTxnId(): number { return _currentTxnId; },
  get activeTravelCount(): number { return _activeTravels.size; },
};

// ─── Private helper ───────────────────────────────────────────
function _findTilePlacement(tileId: number, txnId: number): TilePlacementRecord | undefined {
  for (let i = _tilePlacements.length - 1; i >= 0; i--) {
    const r = _tilePlacements[i];
    if (r.tileId === tileId && r.txnId === txnId) return r;
  }
  return undefined;
}

function _computeSampleIntervals(a: ActionTimingRecord): {
  commit_req:        number | null;
  req_ws:            number | null;
  ws_applyGS:        number | null;
  applyGS_render:    number | null;
  render_tween:      number | null;
  tween_firstFrame:  number | null;
  ws_firstFrame:     number | null;
  commit_firstFrame: number | null;
  rest:              number | null;
} {
  const d = (x?: number, y?: number): number | null =>
    x !== undefined && y !== undefined ? Math.round(y - x) : null;
  return {
    commit_req:        d(a.releaseTs,               a.requestSentTs),
    req_ws:            d(a.requestSentTs,            a.wsUpdateTs),
    ws_applyGS:        d(a.wsUpdateTs,               a.applyGameStateStartedAt),
    applyGS_render:    d(a.applyGameStateStartedAt,  a.boardRenderStartedAt),
    render_tween:      d(a.boardRenderStartedAt,     a.travelTweenCreatedAt),
    tween_firstFrame:  d(a.travelTweenCreatedAt,     a.travelFirstUpdateAt),
    ws_firstFrame:     d(a.wsUpdateTs,               a.travelFirstUpdateAt),
    commit_firstFrame: d(a.releaseTs,                a.travelFirstUpdateAt),
    rest:              d(a.requestSentTs,             a.restResponseTs),
  };
}

function _extStats(vals: number[]): {
  n: number; min: number; avg: number; p50: number; p90: number; p95: number; max: number;
} | null {
  if (!vals.length) return null;
  const s   = [...vals].sort((a, b) => a - b);
  const pct = (p: number) =>
    Math.round(s[Math.min(Math.floor(s.length * p / 100), s.length - 1)]);
  return {
    n:   s.length,
    min: Math.round(s[0]),
    avg: Math.round(s.reduce((acc, v) => acc + v, 0) / s.length),
    p50: pct(50),
    p90: pct(90),
    p95: pct(95),
    max: Math.round(s[s.length - 1]),
  };
}

// ─── window.__DOMINO_DIAGNOSTICS__ ───────────────────────────
export interface DominoDiagnosticsApi {
  dumpBoard():                         void;
  dumpTransactions():                  void;
  dumpMisplacements():                 void;
  dumpPerformance():                   void;
  dumpPreTravelLatency():              void;
  preTravelSummary():                  void;
  exportPreTravelLatency(): Record<string, unknown>;
  dumpExitHistory():                   void;
  dumpStateAcceptance():               void;
  clear():                             void;
  summary():                           void;
}

if (GAME_DIAGNOSTICS) {
  const api: DominoDiagnosticsApi = {

    dumpBoard(): void {
      if (!_boardRef) {
        console.warn('[DominoDiagnostics] No board reference — attach() not called or scene destroyed.');
        return;
      }
      const sprites = _boardRef.getTileSprites();
      if (sprites.size === 0) { console.log('[DominoDiagnostics] Board has no tile sprites.'); return; }

      const boardUnit = getBoardUnitSize();
      const rows: Record<string, unknown>[] = [];

      for (const [id, sp] of sprites) {
        const coords = _tileEngineCoords.get(id);
        const worldX = _boardRef.x + sp.x * _boardRef.scaleX;
        const worldY = _boardRef.y + sp.y * _boardRef.scaleY;
        const expLocalX = coords ? coords.engineX * boardUnit : null;
        const expLocalY = coords ? coords.engineY * boardUnit : null;
        const activeTweens = sp.scene?.tweens?.getTweensOf?.(sp)?.length ?? '?';
        rows.push({
          tileId:       id,
          engineX:      coords?.engineX ?? '?',
          engineY:      coords?.engineY ?? '?',
          expLocalX:    expLocalX !== null ? expLocalX.toFixed(1) : '?',
          expLocalY:    expLocalY !== null ? expLocalY.toFixed(1) : '?',
          actualLocalX: sp.x.toFixed(1),
          actualLocalY: sp.y.toFixed(1),
          dLocalX:      expLocalX !== null ? (sp.x - expLocalX).toFixed(2) : '?',
          dLocalY:      expLocalY !== null ? (sp.y - expLocalY).toFixed(2) : '?',
          worldX:       worldX.toFixed(1),
          worldY:       worldY.toFixed(1),
          boardUnit,
          boardScale:   _boardRef.scaleX.toFixed(3),
          activeTweens,
          alpha:        sp.alpha,
          visible:      sp.visible,
        });
      }
      console.log('[DominoDiagnostics] Board tile state:');
      console.table(rows);
    },

    dumpTransactions(): void {
      if (_txnLog.length === 0) { console.log('[DominoDiagnostics] No transactions recorded.'); return; }
      console.log('[DominoDiagnostics] Transaction log:');
      console.table(_txnLog.map(t => ({
        txnId:         t.txnId,
        source:        t.source,
        turn:          t.currentTurn,
        tiles:         t.tileCount,
        newTiles:      t.newTileIds.join(',') || '-',
        unit:          t.boardUnit,
        prevUnit:      t.prevBoardUnit ?? '-',
        autoFitDx:      t.boardBefore && t.boardAfter ? (t.boardAfter.x - t.boardBefore.x).toFixed(1) : '-',
        autoFitDscale:  t.boardBefore && t.boardAfter ? (t.boardAfter.scaleX - t.boardBefore.scaleX).toFixed(4) : '-',
        activeTween:    t.hadActiveTravelTween,
        resizeDuring:   t.resizeOccurredDuringTx,
        rsSnapped:      t.resizeTravelSnapped       ?? '-',
        rsReproj:       t.resizeReprojected         ?? '-',
        rsViolations:   t.resizeInvariantViolations ?? '-',
        wsRevBefore:    t.wsRevBeforeSubscription    ?? '-',
        wsRevAfterSub:  t.wsRevAfterSubscription     ?? '-',
        wsRevResp:      t.wsRevAtResponse            ?? '-',
        wsDuringSub:    t.wsAppliedDuringSubscription ?? '-',
        wsDuringREST:   t.wsAppliedDuringRestAwait   ?? '-',
        wsAppliedAny:   t.wsAppliedAny               ?? '-',
        restDiscarded:  t.restDiscarded              ?? false,
        reason:         t.rejectionReason            ?? '-',
        restGN:         t.gameNumber                 ?? '-',
        wsGN:           t.wsStateGameNumber          ?? '-',
      })));
    },

    dumpMisplacements(): void {
      const misplaced   = _tilePlacements.filter(r => r.misplacement);
      const boardMoved  = _detections.filter(d =>
        d.type === 'BOARD_MOVED_DURING_TRAVEL' || d.type === 'BOARD_SCALE_CHANGED_DURING_TRAVEL');
      const staleRest   = _detections.filter(d => d.type === 'REST_OLDER_THAN_WS');
      const discarded   = _detections.filter(d => d.type === 'REST_PUBLIC_STATE_DISCARDED_AFTER_WS');
      const resizeDur   = _detections.filter(d => d.type === 'RESIZE_DURING_TRAVEL');
      const staleTween  = _detections.filter(d => d.type === 'STALE_TWEEN_AFTER_NEWER_TXN');
      const staleUnit   = _detections.filter(d => d.type === 'STALE_UNIT_SIZE_AFTER_RESIZE');

      console.group('[DominoDiagnostics] Misplacements & Anomalies');

      if (misplaced.length) {
        console.log(`${misplaced.length} MISPLACED tile(s):`);
        console.table(misplaced.map(r => ({
          tileId:     r.tileId,
          txnId:      r.txnId,
          ghostDest:  `(${r.ghostDestX?.toFixed(1)}, ${r.ghostDestY?.toFixed(1)})`,
          finalWorld: `(${r.finalWorldX?.toFixed(1)}, ${r.finalWorldY?.toFixed(1)})`,
          ΔX:         r.destDeltaX?.toFixed(2),
          ΔY:         r.destDeltaY?.toFixed(2),
        })));
      } else { console.log('No misplaced tiles.'); }

      if (boardMoved.length) {
        console.log(`${boardMoved.length} board-moved-during-travel event(s):`);
        console.table(boardMoved.map(d => ({ type: d.type, ...d.detail })));
      }
      if (staleRest.length) {
        console.log(`${staleRest.length} REST-older-than-WS event(s) (diagnostic only — not the ordering authority):`);
        console.table(staleRest.map(d => d.detail));
      }
      if (discarded.length) {
        console.log(`${discarded.length} REST-public-state-discarded event(s):`);
        console.table(discarded.map(d => d.detail));
      }
      if (resizeDur.length) {
        console.log(`${resizeDur.length} resize-during-travel event(s).`);
      }
      if (staleTween.length) {
        console.log(`${staleTween.length} stale-tween event(s):`);
        console.table(staleTween.map(d => d.detail));
      }
      if (staleUnit.length) {
        console.log(`${staleUnit.length} stale-unit-size event(s):`);
        console.table(staleUnit.map(d => d.detail));
      }
      console.groupEnd();
    },

    dumpPerformance(): void {
      const canvas     = document.querySelector('canvas');
      const frameStats = _computeFrameStats();

      const snapshot: Record<string, unknown> = {
        avgFps:           frameStats ? frameStats.avg.toFixed(1) : 'n/a',
        p5Fps:            frameStats ? frameStats.p5.toFixed(1)  : 'n/a',
        maxFrameTime:     frameStats ? `${frameStats.maxDelta.toFixed(1)} ms` : 'n/a',
        medFrameTime:     frameStats ? `${frameStats.medDelta.toFixed(1)} ms` : 'n/a',
        spikes25ms:       frameStats?.spikes25  ?? 'n/a',
        spikes50ms:       frameStats?.spikes50  ?? 'n/a',
        spikes100ms:      frameStats?.spikes100 ?? 'n/a',
        phaserActualFps:  _gameRef?.loop?.actualFps?.toFixed(1) ?? 'n/a',
        phaserDelta:      _gameRef?.loop?.delta !== undefined ? `${_gameRef.loop.delta.toFixed(1)} ms` : 'n/a',
        activeTravels:    _activeTravels.size,
        boardTileCount:   _boardRef?.getTileSprites().size ?? 'n/a',
        displayListCount: _sceneRef?.children?.list?.length ?? 'n/a',
        cameraZoom:       _sceneRef?.cameras?.main?.zoom ?? 'n/a',
        canvasCssW:       canvas?.style?.width ?? 'n/a',
        canvasCssH:       canvas?.style?.height ?? 'n/a',
        canvasPixelW:     canvas?.width ?? 'n/a',
        canvasPixelH:     canvas?.height ?? 'n/a',
        devicePixelRatio: window.devicePixelRatio,
        boardX:           _boardRef?.x?.toFixed(1) ?? 'n/a',
        boardY:           _boardRef?.y?.toFixed(1) ?? 'n/a',
        boardScaleX:      _boardRef?.scaleX?.toFixed(3) ?? 'n/a',
        boardScaleY:      _boardRef?.scaleY?.toFixed(3) ?? 'n/a',
        boardUnit:        getBoardUnitSize(),
      };
      console.log('[DominoDiagnostics] Performance snapshot:');
      console.table([snapshot]);

      if (_actionLog.length > 0) {
        const last5 = _actionLog.slice(-5);
        console.log('[DominoDiagnostics] Last 5 action timings (ms from pointerDown):');
        console.table(last5.map(a => ({
          tileId:       a.tileId ?? '-',
          ptr_lift:     _delta(a.pointerDownTs, a.liftStartTs),
          lift_release: _delta(a.liftStartTs,   a.releaseTs),
          release_req:  _delta(a.releaseTs,      a.requestSentTs),
          req_rest:     _delta(a.requestSentTs,  a.restResponseTs),
          req_ws:       _delta(a.requestSentTs,  a.wsUpdateTs),
          ws_render:    _delta(a.wsUpdateTs,      a.renderStartTs),
          travel_ms:    _delta(a.travelStartTs,   a.travelEndTs),
          ptr_settle:   _delta(a.pointerDownTs,   a.settleTs),
        })));
      }
    },

    exportPreTravelLatency(): Record<string, unknown> {
      // ── Environment ────────────────────────────────────────────────────
      const buildHash = (() => {
        try {
          const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
          for (const s of scripts) {
            const m = s.src.match(/index-([A-Za-z0-9_-]+)\.js/);
            if (m) return m[1];
          }
        } catch { /* ignore */ }
        return 'unknown';
      })();

      const fs     = _computeFrameStats();
      const winObj = window as unknown as Record<string, unknown>;
      const phasObj = typeof winObj['Phaser'] === 'object'
        ? (winObj['Phaser'] as Record<string, unknown>)
        : null;

      const meta: Record<string, unknown> = {
        exportedAt:      new Date().toISOString(),
        buildHash,
        browser:         navigator.userAgent,
        devicePixelRatio: window.devicePixelRatio,
        viewport:        { width: window.innerWidth, height: window.innerHeight },
        fps:             fs
          ? { avg: +fs.avg.toFixed(1), p5: +fs.p5.toFixed(1), maxFrameMs: +fs.maxDelta.toFixed(1) }
          : null,
        phaserVersion:   phasObj?.['VERSION'] ?? 'unknown',
        phaserActualFps: _gameRef?.loop?.actualFps != null
          ? +_gameRef.loop.actualFps.toFixed(1)
          : null,
      };

      // ── Sample groups ──────────────────────────────────────────────────
      // Also reject samples with physically impossible negative intervals — these
      // indicate a stale _activeAction that survived a missed cleanup before the
      // hardening fix and should not skew statistics.
      const complete = _actionLog.filter(a => {
        if (a.wsUpdateTs === undefined || a.travelFirstUpdateAt === undefined) return false;
        const iv = _computeSampleIntervals(a);
        return (
          (iv.ws_firstFrame   === null || iv.ws_firstFrame   >= 0) &&
          (iv.tween_firstFrame === null || iv.tween_firstFrame >= 0) &&
          (iv.req_ws           === null || iv.req_ws           >= 0)
        );
      });
      const myMoves  = complete.filter(a => !a.isOpponentMove);
      const oppMoves = complete.filter(a =>  a.isOpponentMove === true);
      const clicks   = myMoves.filter(a => a.inputMethod === 'click');
      const drags    = myMoves.filter(a => a.inputMethod === 'drag');

      const summary: Record<string, unknown> = {
        totalSamples:  complete.length,
        localMoves:    myMoves.length,
        opponentMoves: oppMoves.length,
        clickMoves:    clicks.length,
        dragMoves:     drags.length,
        unknownMethod: myMoves.filter(a => !a.inputMethod).length,
      };

      if (complete.length === 0) {
        const result: Record<string, unknown> = {
          meta, summary, intervals: null, outliers: null, checklist: null, rawSamples: [],
        };
        console.log('[DominoDiagnostics] exportPreTravelLatency(): no complete samples yet.');
        console.log(JSON.stringify(result, null, 2));
        return result;
      }

      // ── Stats helpers ──────────────────────────────────────────────────
      const si = _computeSampleIntervals;
      const statsFor = (
        arr: ActionTimingRecord[],
        fn:  (a: ActionTimingRecord) => number | null,
      ) => {
        const vals = arr.map(fn).filter((v): v is number => v !== null);
        return _extStats(vals);
      };

      // ── Intervals ──────────────────────────────────────────────────────
      const intervals: Record<string, unknown> = {
        ws_to_firstFrame_all:           statsFor(complete, a => si(a).ws_firstFrame),
        ws_to_firstFrame_local:         statsFor(myMoves,  a => si(a).ws_firstFrame),
        ws_to_firstFrame_opponent:      statsFor(oppMoves, a => si(a).ws_firstFrame),
        commit_to_firstFrame_local:     statsFor(myMoves,  a => si(a).commit_firstFrame),
        commit_to_firstFrame_click:     statsFor(clicks,   a => si(a).commit_firstFrame),
        commit_to_firstFrame_drag:      statsFor(drags,    a => si(a).commit_firstFrame),
        network_req_to_ws:              statsFor(myMoves,  a => si(a).req_ws),
        rest_round_trip:                statsFor(myMoves,  a => si(a).rest),
        commit_to_request:              statsFor(myMoves,  a => si(a).commit_req),
        client_ws_to_applyGS:           statsFor(complete, a => si(a).ws_applyGS),
        client_applyGS_to_boardRender:  statsFor(complete, a => si(a).applyGS_render),
        client_boardRender_to_tween:    statsFor(complete, a => si(a).render_tween),
        client_tween_to_firstFrame:     statsFor(complete, a => si(a).tween_firstFrame),
      };

      // ── Raw samples ────────────────────────────────────────────────────
      const toRow = (a: ActionTimingRecord): Record<string, unknown> => {
        const s = si(a);
        return {
          tileId:            a.tileId ?? null,
          method:            a.isOpponentMove ? 'opponent' : (a.inputMethod ?? 'unknown'),
          commit_req:        s.commit_req,
          req_ws:            s.req_ws,
          ws_applyGS:        s.ws_applyGS,
          applyGS_render:    s.applyGS_render,
          render_tween:      s.render_tween,
          tween_firstFrame:  s.tween_firstFrame,
          ws_firstFrame:     s.ws_firstFrame,
          commit_firstFrame: s.commit_firstFrame,
          rest:              s.rest,
        };
      };
      const rawSamples = complete.map(toRow);

      // ── Outliers ───────────────────────────────────────────────────────
      const withWsFirst = complete
        .map(a => ({ a, v: si(a).ws_firstFrame }))
        .filter((x): x is { a: ActionTimingRecord; v: number } => x.v !== null)
        .sort((x, y) => y.v - x.v);

      const withNet = myMoves
        .map(a => ({ a, v: si(a).req_ws }))
        .filter((x): x is { a: ActionTimingRecord; v: number } => x.v !== null)
        .sort((x, y) => y.v - x.v);

      const outliers: Record<string, unknown> = {
        slowest_ws_to_firstFrame: withWsFirst.length > 0
          ? toRow(withWsFirst[0].a) : null,
        fastest_ws_to_firstFrame: withWsFirst.length > 0
          ? toRow(withWsFirst[withWsFirst.length - 1].a) : null,
        slowest_network: withNet.length > 0
          ? toRow(withNet[0].a) : null,
        high_client_latency:  withWsFirst.filter(x => x.v > 40) .map(x => toRow(x.a)),
        high_network_latency: withNet    .filter(x => x.v > 300).map(x => toRow(x.a)),
      };

      // ── Validation checklist ───────────────────────────────────────────
      const checklist = {
        clickPathMeasured:     clicks.length  > 0,
        dragPathMeasured:      drags.length   > 0,
        opponentMovesMeasured: oppMoves.length > 0,
        ownMovesMeasured:      myMoves.length  > 0,
      };

      // ── Package ────────────────────────────────────────────────────────
      const result: Record<string, unknown> = {
        meta, summary, intervals, outliers, checklist, rawSamples,
      };

      console.log('[DominoDiagnostics] exportPreTravelLatency() — copy JSON below, or right-click the object → "Copy object":');
      console.log(JSON.stringify(result, null, 2));
      console.log(result);
      return result;
    },

    dumpStateAcceptance(): void {
      if (_stateAcceptLog.length === 0) {
        console.log('[DominoDiagnostics] No authoritative-state records yet.');
        return;
      }
      const rejected = _stateAcceptLog.filter(r => !r.accepted).length;
      console.log(`[DominoDiagnostics] ${_stateAcceptLog.length} state record(s), ${rejected} rejected:`);
      console.table(_stateAcceptLog.map(r => ({
        t:        r.ts.toFixed(0),
        src:      r.source,
        game:     r.gameId,
        seq:      r.stateSeq ?? '-',
        last:     r.lastAppliedBefore,
        ok:       r.accepted,
        reason:   r.reason,
        turn:     r.turn ?? '-',
        tiles:    r.tileCount,
        scene:    r.sceneInstanceId,
        vis:      r.visibility,
        ws:       r.wsConnected,
        first:    r.firstRender,
        noAnim:   r.animationsSuppressed,
      })));
    },

    dumpExitHistory(): void {
      const history = readGameExitHistory();
      if (history.length === 0) {
        console.log('[DominoDiagnostics] No exit breadcrumbs recorded on this device.');
        return;
      }
      console.log(`[DominoDiagnostics] ${history.length} game-exit breadcrumb(s), oldest first:`);
      console.table(history.map(b => ({
        ts:      b.ts,
        reason:  b.reason,
        note:    b.note ?? '-',
        game:    b.gameId ?? '-',
        session: b.sessionId ?? '-',
        ws:      b.wsState,
        auth:    b.authState,
        online:  b.online,
        vis:     b.visibility,
      })));
      console.log(JSON.stringify(history, null, 2));
    },

    dumpPreTravelLatency(): void {
      if (_actionLog.length === 0) {
        console.log('[DominoDiagnostics] No move records yet. Play some tiles to populate data.');
        return;
      }
      console.log('[DominoDiagnostics] Pre-travel latency — full log (all times in ms):');
      console.table(_actionLog.map(a => ({
        tileId:            a.tileId ?? '-',
        method:            a.isOpponentMove ? 'opponent' : (a.inputMethod ?? '-'),
        commit_req:        _delta(a.releaseTs,               a.requestSentTs),
        req_ws:            _delta(a.requestSentTs,           a.wsUpdateTs),
        ws_applyGS:        _delta(a.wsUpdateTs,              a.applyGameStateStartedAt),
        applyGS_render:    _delta(a.applyGameStateStartedAt, a.boardRenderStartedAt),
        render_tween:      _delta(a.boardRenderStartedAt,    a.travelTweenCreatedAt),
        tween_firstFrame:  _delta(a.travelTweenCreatedAt,    a.travelFirstUpdateAt),
        ws_firstFrame:     _delta(a.wsUpdateTs,              a.travelFirstUpdateAt),
        commit_firstFrame: _delta(a.releaseTs,               a.travelFirstUpdateAt),
        req_rest:          _delta(a.requestSentTs,           a.restResponseTs),
      })));
    },

    preTravelSummary(): void {
      const complete = _actionLog.filter(
        a => a.wsUpdateTs !== undefined && a.travelFirstUpdateAt !== undefined,
      );
      if (complete.length === 0) {
        console.log('[DominoDiagnostics] No complete records yet. Play several tiles first.');
        return;
      }

      const myMoves  = complete.filter(a => !a.isOpponentMove);
      const oppMoves = complete.filter(a =>  a.isOpponentMove);

      function _nums(
        arr: ActionTimingRecord[],
        fn: (a: ActionTimingRecord) => number | undefined,
      ): number[] {
        return arr.map(fn).filter((v): v is number => v !== undefined);
      }

      function _stats(
        vals: number[],
      ): { p50: number; p90: number; max: number; n: number } {
        if (!vals.length) return { p50: 0, p90: 0, max: 0, n: 0 };
        const s   = [...vals].sort((a, b) => a - b);
        const pct = (p: number) =>
          Math.round(s[Math.min(Math.floor(s.length * p / 100), s.length - 1)]);
        return { p50: pct(50), p90: pct(90), max: Math.round(s[s.length - 1]), n: s.length };
      }

      const wsToFirst     = _nums(complete, a => a.travelFirstUpdateAt! - a.wsUpdateTs!);
      const netEst        = _nums(myMoves, a =>
        a.requestSentTs !== undefined ? a.wsUpdateTs! - a.requestSentTs : undefined);
      const clientAll     = _nums(complete, a =>
        a.applyGameStateStartedAt !== undefined
          ? a.travelFirstUpdateAt! - a.applyGameStateStartedAt
          : undefined);
      const commitToFirst = _nums(myMoves, a =>
        a.releaseTs !== undefined ? a.travelFirstUpdateAt! - a.releaseTs : undefined);

      console.log(
        `[DominoDiagnostics] Pre-Travel Latency Summary` +
        `  (${complete.length} moves: ${myMoves.length} mine, ${oppMoves.length} opponent)`,
      );
      console.table([
        { interval: 'WS receipt → first visible frame (all)',    ..._stats(wsToFirst) },
        { interval: 'Commit → first visible frame (my moves)',   ..._stats(commitToFirst) },
        { interval: 'Network estimate: requestSent → WS',        ..._stats(netEst) },
        { interval: 'Client-side: applyGameState → first frame', ..._stats(clientAll) },
      ]);

      const phases = complete.filter(
        a =>
          a.applyGameStateStartedAt !== undefined &&
          a.boardRenderStartedAt    !== undefined &&
          a.travelTweenCreatedAt    !== undefined &&
          a.travelFirstUpdateAt     !== undefined,
      );
      if (phases.length > 0) {
        console.log(`  Phase breakdown (${phases.length} fully-instrumented moves, ms):`);
        console.table([
          { phase: 'WS → applyGameState start',    ..._stats(_nums(phases, a => a.applyGameStateStartedAt! - a.wsUpdateTs!)) },
          { phase: 'applyGameState → boardRender', ..._stats(_nums(phases, a => a.boardRenderStartedAt!    - a.applyGameStateStartedAt!)) },
          { phase: 'boardRender → tweenCreated',   ..._stats(_nums(phases, a => a.travelTweenCreatedAt!    - a.boardRenderStartedAt!)) },
          { phase: 'tweenCreated → first frame',   ..._stats(_nums(phases, a => a.travelFirstUpdateAt!     - a.travelTweenCreatedAt!)) },
        ]);
      }

      const clickVals = _nums(
        myMoves.filter(a => a.inputMethod === 'click'),
        a => a.releaseTs !== undefined ? a.travelFirstUpdateAt! - a.releaseTs : undefined,
      );
      const dragVals = _nums(
        myMoves.filter(a => a.inputMethod === 'drag'),
        a => a.releaseTs !== undefined ? a.travelFirstUpdateAt! - a.releaseTs : undefined,
      );
      if (clickVals.length > 0 || dragVals.length > 0) {
        console.log('  By input method — commit → first frame (ms):');
        console.table([
          { method: 'click', ..._stats(clickVals) },
          { method: 'drag',  ..._stats(dragVals) },
        ]);
      }
    },

    clear(): void {
      _txnLog.length        = 0;
      _tilePlacements.length = 0;
      _detections.length    = 0;
      _framePerf.length     = 0;
      _actionLog.length     = 0;
      _activeTravels.clear();
      _tileEngineCoords.clear();
      _currentTxnId     = -1;
      _txnCounter       = 0;
      _renderInProgress = false;
      _lastWsTileCount  = -1;
      _activeAction     = null;
      console.log('[DominoDiagnostics] All records cleared.');
    },

    summary(): void {
      const misplaced   = _tilePlacements.filter(r => r.misplacement).length;
      const boardMoved  = _detections.filter(d =>
        d.type === 'BOARD_MOVED_DURING_TRAVEL' || d.type === 'BOARD_SCALE_CHANGED_DURING_TRAVEL').length;
      const staleRest   = _detections.filter(d => d.type === 'REST_OLDER_THAN_WS').length;
      const restDiscard = _detections.filter(d => d.type === 'REST_PUBLIC_STATE_DISCARDED_AFTER_WS').length;
      const resizeDur       = _detections.filter(d => d.type === 'RESIZE_DURING_TRAVEL').length;
      const staleTween      = _detections.filter(d => d.type === 'STALE_TWEEN_AFTER_NEWER_TXN').length;
      const staleUnit       = _detections.filter(d => d.type === 'STALE_UNIT_SIZE_AFTER_RESIZE').length;
      const invariantViolat = _detections.filter(d => d.type === 'RESIZE_INVARIANT_VIOLATION').length;
      const resizeTxns      = _txnLog.filter(t => t.source === 'resize');
      const totalReproj     = resizeTxns.reduce((s, t) => s + (t.resizeReprojected ?? 0), 0);
      const tweensSnapped   = resizeTxns.filter(t => t.resizeTravelSnapped).length;
      const fs              = _computeFrameStats();

      const deltas = _tilePlacements
        .filter(r => r.destDeltaX !== undefined)
        .map(r => Math.sqrt((r.destDeltaX ?? 0) ** 2 + (r.destDeltaY ?? 0) ** 2));
      const avgDelta = deltas.length
        ? (deltas.reduce((s, v) => s + v, 0) / deltas.length).toFixed(2)
        : 'n/a';
      const maxDelta = deltas.length ? Math.max(...deltas).toFixed(2) : 'n/a';

      console.log('[DominoDiagnostics] Summary:');
      console.table([{
        transactions:           _txnLog.length,
        tilesTracked:           _tilePlacements.length,
        misplacedTiles:         misplaced,
        boardMovedDuringTravel: boardMoved,
        resizeDuringTravel:     resizeDur,
        staleRestEvents:        staleRest,
        restDiscardedEvents:    restDiscard,
        staleTweenEvents:       staleTween,
        staleUnitEvents:        staleUnit,
        resizeTxns:             resizeTxns.length,
        tilesReprojectd:        totalReproj,
        tweensSnapped:          tweensSnapped,
        resizeInvariantViolat:  invariantViolat,
        avgGhostDelta:          avgDelta,
        maxGhostDelta:          maxDelta,
        avgFps:                 fs ? fs.avg.toFixed(1) : 'n/a',
        p5Fps:                  fs ? fs.p5.toFixed(1)  : 'n/a',
        maxFrameTime:           fs ? `${fs.maxDelta.toFixed(1)} ms` : 'n/a',
        frameSamples:           _framePerf.length,
        activeTravels:          _activeTravels.size,
      }]);
    },
  };

  (window as Window & { __DOMINO_DIAGNOSTICS__?: DominoDiagnosticsApi }).__DOMINO_DIAGNOSTICS__ = api;

  console.log(
    '[DominoDiagnostics] Active. Commands:\n' +
    '  window.__DOMINO_DIAGNOSTICS__.summary()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpBoard()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpTransactions()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpMisplacements()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpPerformance()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpPreTravelLatency()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.preTravelSummary()\n' +
    '  window.__DOMINO_DIAGNOSTICS__.exportPreTravelLatency()  ← JSON export for analysis\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpExitHistory()  ← game-exit breadcrumbs (persisted)\n' +
    '  window.__DOMINO_DIAGNOSTICS__.dumpStateAcceptance()  ← state-sequence gate verdicts\n' +
    '  window.__DOMINO_DIAGNOSTICS__.clear()',
  );
}

function _delta(a?: number, b?: number): string {
  return a !== undefined && b !== undefined ? (b - a).toFixed(0) : '-';
}
